/**
 * Story 062 — Dynamic Specifications parameter-array sub-form
 * (UX brief §4 + §5; Add modal AC14/AC15/AC16/AC17, View/Edit modal
 * AC9/AC10).
 *
 * The sub-form is keyed on the resolved component-group `groupCode`:
 * picking a group seeds N default `{parameter, unit, value}` rows from
 * the shared `defaultsForGroupCode` map (from `@aeriosiq/ui`). Subsequent
 * group switches fire a parent-owned confirmation dialog (Story 062
 * AC16) — this component does not own the confirm modal; the parent
 * `ComponentGroupAutocomplete` handles it and only re-seeds defaults
 * after the confirm.
 *
 * Each row is a `<div role="row" aria-label={parameter}>` so AC15 /
 * AC16's `specsRegion.getByRole("row", { name: /length/i })` matches
 * once defaults render OR the user types in the Parameter input. The
 * parameter caption is rendered via a visually-hidden span so
 * `specsRegion.getByText(/^length$/i)` finds it without a duplicate
 * visible label.
 *
 * Driven by React Hook Form `useFieldArray` on a single
 * `specifications` field. The parent caller is expected to provide
 * RHF `control`.
 *
 * ASM-SPEC-01 D-8 (2026-09-22) — Parameter and Unit use the same
 * `<CreatableCombobox>` as the turbine-models editor so the two screens stay
 * visually identical, but with the STATIC lists only: the parameter
 * suggestions are this group's own defaults and the units are
 * `COMMON_UNITS`. No catalogue call — wiring component models to
 * `listAssetModelSpecificationParameters` is ASM-SPEC-02. Vocabulary open as
 * before; widths, row roles, captions and the empty-row drop unchanged.
 */
import type { ReactElement } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  useController,
  useFieldArray,
  useWatch,
  type Control,
  type UseFormRegister,
} from "react-hook-form";
import { CircleMinusIcon, PlusIcon } from "@aeriosiq/ui/icons";
import { CreatableCombobox, type CreatableComboboxOption } from "@aeriosiq/ui";

/** FE datalist of common units — surfaced as `<datalist>` hints on the
 *  Unit input. Open vocabulary; the user may type anything else. */
const COMMON_UNITS = [
  "meter",
  "centimeter",
  "kilogram",
  "kilowatt",
  "text",
  "celsius",
  "percent",
] as const;

const UNIT_OPTIONS: CreatableComboboxOption[] = COMMON_UNITS.map((u) => ({
  value: u,
}));

/** The Parameter + Unit pair of one row — the turbine-models twin (D-8),
 *  minus the catalogue. `useController` for the same reason: the pick has to
 *  write the sibling unit field. */
function ParameterAndUnitFields({
  control,
  index,
  readOnly,
  parameterOptions,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  index: number;
  readOnly: boolean;
  parameterOptions: readonly CreatableComboboxOption[];
}): ReactElement {
  const parameter = useController({
    control,
    name: `specifications.${String(index)}.parameter`,
  });
  const unit = useController({
    control,
    name: `specifications.${String(index)}.unit`,
  });
  return (
    <>
      <CreatableCombobox
        value={(parameter.field.value as string | undefined) ?? ""}
        options={parameterOptions}
        ariaLabel="parameter"
        placeholder="Parameter"
        width={200}
        readOnly={readOnly}
        onChange={(next) => {
          parameter.field.onChange(next);
        }}
        onOptionSelected={(option) => {
          unit.field.onChange(option.secondary ?? "");
        }}
      />
      <CreatableCombobox
        value={(unit.field.value as string | undefined) ?? ""}
        options={UNIT_OPTIONS}
        ariaLabel="unit"
        placeholder="Unit"
        width={160}
        readOnly={readOnly}
        onChange={(next) => {
          unit.field.onChange(next);
        }}
      />
    </>
  );
}

export interface SpecificationsFieldProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>;
  readOnly?: boolean;
}

export function SpecificationsField({
  control,
  register,
  readOnly = false,
}: SpecificationsFieldProps): ReactElement {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "specifications",
  });

  // useFieldArray's `fields` carry only INITIAL values; the
  // user-typed values need to be read via `useWatch` so the
  // accessible row name + the visible parameter-name caption update
  // live (drives AC15/AC16's `getByRole("row", { name: /length/i })`
  // and `getByText(/^length$/i)`).
  const watchedRows =
    (useWatch({
      control,
      name: "specifications",
    }) as
      | { parameter?: string; unit?: string; value?: string }[]
      | undefined) ?? [];

  // The parameter suggestions are the rows this group seeded — the same
  // static vocabulary `defaultsForGroupCode` put on screen. Nothing is
  // fetched (D-8); a row the user typed themselves becomes a suggestion for
  // the next row, which is the whole of the offer here.
  const parameterOptions: CreatableComboboxOption[] = [
    ...new Map(
      watchedRows
        .map((r) => ({
          value: (r.parameter ?? "").trim(),
          secondary: r.unit ?? "",
        }))
        .filter((o) => o.value.length > 0)
        .map((o) => [o.value, o] as const),
    ).values(),
  ];

  return (
    <Box
      component="section"
      role="region"
      aria-label="Specifications"
      sx={{ display: "block" }}
    >
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        Specifications
      </Typography>

      <Stack spacing={1}>
        {fields.length === 0 ? (
          <Typography
            variant="body2"
            color="text.tertiary"
            sx={{ fontStyle: "italic" }}
          >
            {readOnly
              ? "No specifications."
              : "Pick a Component Group above to seed defaults, or use “+ Add parameter” below to add rows."}
          </Typography>
        ) : (
          fields.map((field, index) => {
            const watched = watchedRows[index] ?? {};
            // Prefer the watched value (live edits) but fall back to
            // the initial default so the accessible name + visible
            // caption are non-empty on the first render after defaults
            // seed.
            const parameterText =
              watched.parameter ??
              (field as unknown as { parameter?: string }).parameter ??
              "";
            const accessibleRowName =
              parameterText.length > 0
                ? parameterText
                : `Parameter ${String(index + 1)}`;
            return (
              <Box
                key={field.id}
                role="row"
                aria-label={accessibleRowName}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                  position: "relative",
                }}
              >
                {/* Visually-hidden visible parameter caption — AC15
                    reads `specsRegion.getByText(/^length$/i)` to verify
                    the seeded default rows render. The caption mirrors
                    the Parameter input's value live (via useWatch) so
                    it stays in sync when the user edits the name.
                    Visually-hidden via the clip rectangle so the row
                    isn't visually duplicated. */}
                {parameterText.length > 0 ? (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      position: "absolute",
                      width: "1px",
                      height: "1px",
                      overflow: "hidden",
                      clip: "rect(0 0 0 0)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {parameterText}
                  </Typography>
                ) : null}
                <ParameterAndUnitFields
                  control={control}
                  index={index}
                  readOnly={readOnly}
                  parameterOptions={parameterOptions}
                />
                <TextField
                  {...register(`specifications.${String(index)}.value`)}
                  size="small"
                  placeholder="Value"
                  sx={{ flex: 1, minWidth: 160 }}
                  slotProps={{
                    htmlInput: {
                      "aria-label": "value",
                      ...(readOnly ? { readOnly: true } : {}),
                    },
                  }}
                />
                {!readOnly ? (
                  <IconButton
                    aria-label={`Remove parameter ${
                      parameterText || String(index + 1)
                    }`}
                    size="small"
                    onClick={() => {
                      remove(index);
                    }}
                  >
                    <CircleMinusIcon fontSize="small" />
                  </IconButton>
                ) : null}
              </Box>
            );
          })
        )}
      </Stack>

      {!readOnly ? (
        <Button
          size="small"
          variant="text"
          startIcon={<PlusIcon fontSize="small" />}
          onClick={() => {
            append({ parameter: "", unit: "", value: "" });
          }}
          sx={{ mt: 1 }}
        >
          + Add parameter
        </Button>
      ) : null}
    </Box>
  );
}
